import type {
  PlaneConstraintType,
  PlaneProjectionType,
  PlaneSolveStatus,
  PlaneTolerancePolicy,
  ReferenceCommandLifecycle,
  ReferenceCommandModeSource,
  ReferenceEntitySemanticType,
} from '../ReferenceFeatureTypes';
import type {PlaneFeatureResult} from './ReferencePlaneSolver';

/**
 * 三参考槽位键值（用于 UI 与命令同步）。
 */
export enum PlaneSlotKey {
  /** 第一参考槽位。 */
  First = 'reference-1',
  /** 第二参考槽位。 */
  Second = 'reference-2',
  /** 第三参考槽位。 */
  Third = 'reference-3',
}


/**
 * 基准面选择引用附加载荷。
 *
 * 设计说明：
 * 1. `entityId` 仍服务于当前会话内的对象回查；
 * 2. 装配体导入拓扑代理必须额外保存稳定拓扑引用，避免后续重算依赖临时 `uuid/name`；
 * 3. 调试字段只用于链路追踪，不作为几何真相。
 */
export interface PlaneSelectionReferencePayload extends Record<string, unknown> {
  /** 原始场景对象 UUID，仅用于排查。 */
  sourceObjectUuid?: string;
  /** 原始场景对象名称，仅用于排查。 */
  sourceObjectName?: string;
  /** 选择层解析出的拓扑命中快照。 */
  topologyPick?: ResolvedTopologyPick | null;
  /** 装配体导入拓扑稳定引用。 */
  assemblyTopologyRef?: AssemblyTopologySelectionRef | null;
}


/**
 * 单个选择候选。
 */
export interface PlaneSelectionCandidate {
  /** 实体 ID。 */
  entityId: string;
  /** 实体类型。 */
  entityType: ReferenceEntitySemanticType;
  /** 展示标签。 */
  label: string;
  /** 选择分类。 */
  kind: ReferenceEntitySemanticType;
  /** 附加几何载荷。 */
  payload?: PlaneSelectionReferencePayload;
}

/**
 * 预览状态。
 */
export interface PlanePreviewState {
  /** 是否预览成功。 */
  ok: boolean;
  /** 成功时的预览结果。 */
  result?: PlaneFeatureResult;
  /** 失败时的错误消息。 */
  error?: string;
}

/**
 * 命令内部状态。
 */
export interface PlaneCommandState {
  /** 命令生命周期。 */
  lifecycle: ReferenceCommandLifecycle;
  /** 约束来源。 */
  modeSource: ReferenceCommandModeSource;
  /** 当前激活槽位。 */
  activeSlot: PlaneSlotKey;
  /** 各槽位绑定的选择引用。 */
  refs: Partial<Record<PlaneSlotKey, PlaneSelectionCandidate | null>>;
  /** 当前约束类型。 */
  constraint: PlaneConstraintType;
  /** 曲面投影策略。 */
  projectionType: PlaneProjectionType | null;
  /** 距离或角度标量。 */
  distanceOrAngle: number;
  /** 是否翻转法向。 */
  reverseNormal: boolean;
  /** 是否翻转偏移方向。 */
  reverseOffset: boolean;
  /** 是否将原点约束在线性参考上。 */
  originOnCurve: boolean;
  /** 目标基准面数量。 */
  planeCount: number;
  /** 当前激活解索引。 */
  solutionIndex: number;
  /** 当前求解状态。 */
  solveStatus: PlaneSolveStatus;
  /** 当前预览结果。 */
  preview: PlanePreviewState;
  /** 是否已发生修改。 */
  dirty: boolean;
}



/**
 * 控制器可选项。
 */
export interface PlaneCommandControllerOptions {
  /** 容差覆盖配置。 */
  tolerance?: Partial<PlaneTolerancePolicy>;
  /** 取消命令回调。 */
  onCancelled?: () => void;
}

/**
 * 命令视图模型。
 */
export interface PlaneCommandViewModel {
  /** 当前命令状态。 */
  state: PlaneCommandState;
  /** 当前是否允许提交。 */
  canSubmit: boolean;
}
